import { useBonuses, useAuth } from 'react-render-hooks';
import { useLocation, useNavigate } from 'react-router';
import cln from 'classnames';
import { AuthInfo } from 'react-memory-optimization/dist/lib/store/user/types';
import { BonusesResponse } from 'react-memory-optimization/dist/lib/store/operations/types';
import {
  DrawerButton,
  BonusGiftSvg,
  useZustandModalKitStore,
  EModalTypes,
  zustandModalKitSelector,
} from 'react-gldn-kit';

import { useZustandCasinoDrawerStore } from 'stores/drawers/casino/store';
import {
  getDrawerStateSelector,
  isFullCasinoDrawerSelector,
  setDrawerStateAction,
} from 'stores/drawers/casino/selectors';
import { useZustandDeviceStore } from 'stores/device/store';
import { isMobileDeviceSelector } from 'stores/device/selectors';

import { useHookFabric } from 'hooks/useHookFabric';
import { sleep } from 'utils/shared';

import { ERouteNames } from 'Routes/types';

import styles from './BonusButtons.module.css';

type Props = {
  closeCallback?: (e?: React.MouseEvent<HTMLDivElement>) => void;
};

const BonusButtons = ({ closeCallback }: Props) => {
  const { pathname } = useLocation();

  const isMobile = useZustandDeviceStore(isMobileDeviceSelector);

  const setCloseState = useZustandCasinoDrawerStore(setDrawerStateAction);

  const closeAndOpen = useZustandModalKitStore(
    zustandModalKitSelector.closeAndOpenModalAction
  );
  const isClosed = useZustandCasinoDrawerStore(getDrawerStateSelector);

  const isAuth = useHookFabric<AuthInfo>({
    fn: useAuth,
  });

  const isFullView = useZustandCasinoDrawerStore(isFullCasinoDrawerSelector);

  const { bonusOnDeposit = [], freeSpins = [] } =
    useHookFabric<BonusesResponse>({
      fn: useBonuses,
      customProps: {
        isActive: true,
      },
    }) || {};

  const navigate = useNavigate();

  const toggleCloseState = async (e?: React.MouseEvent<HTMLDivElement>) => {
    e?.stopPropagation();
    e?.preventDefault();

    if (!isMobile) {
      return;
    }
    setCloseState(!isClosed);
    await sleep(300);
    // Need to prevent animation bag
    setCloseState(false);
    closeCallback?.(e);
  };

  const jumpTo =
    (path: string, isAuthImportant = false) =>
    (e: React.MouseEvent<HTMLDivElement>) => {
      toggleCloseState(e);

      if (!isAuth && isAuthImportant) {
        closeAndOpen(EModalTypes.SignIn, {
          title: 'authorization.title',
        });

        return;
      }

      navigate(path);
    };

  const openModal =
    (
      t: (typeof EModalTypes)[keyof typeof EModalTypes],
      isAuthImportant = false
    ) =>
    (e: React.MouseEvent<HTMLDivElement>) => {
      toggleCloseState(e);

      if (isAuthImportant && !isAuth) {
        closeAndOpen(EModalTypes.SignIn);

        return;
      }
      closeAndOpen(t);
    };

  const isActiveBonuses = bonusOnDeposit.length || freeSpins.length;

  return (
    <DrawerButton
      size="full"
      text="drawers.casino.bonuses"
      handleClick={
        isMobile
          ? openModal(EModalTypes.Bonuses)
          : jumpTo(ERouteNames.ProfileBonusBase, true)
      }
      startIcon={
        <div
          className={cln(styles.box, {
            [styles.indicator]: isActiveBonuses,
          })}
        >
          <BonusGiftSvg classes={styles.giftSvg} />
        </div>
      }
      classes={cln(styles.marginBottom, {
        [styles.active]: pathname === ERouteNames.ProfileBonusBase,
      })}
      isFullView={isFullView}
      centreText={!isFullView}
    />
  );
};

export default BonusButtons;
